import { useTranslation } from 'react-i18next'
import useCustomerInvoice from 'hooks/useCustomerInvoice'
import TextPaper from '@ui/elements/TextPaper'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import { hasTextPaperLines } from '@user/customer/confirmation/getTextPaperStatus'
import { useOrderState } from '@order/hooks/useOrderState'
import { InvoiceFormSchema } from 'utils/vatSchema'
import { useMemo } from 'react'

interface Props {
  onPress: () => void
}

export default function OrderInvoice({ onPress }: Props) {
  const { t } = useTranslation(['ui', 'modals'])
  const { data: invoice } = useCustomerInvoice()
  const enabled = useOrderState((s) => s.order.invoice)

  const first_name = invoice ? invoice?.firstName : ''
  const last_name = invoice ? invoice?.lastName : ''
  const vat = invoice?.vat
  const email = invoice ? invoice?.email : ''
  const mobile = invoice ? invoice?.mobile : ''
  const street = invoice ? invoice?.street : ''
  const street2 = invoice ? invoice?.street2 : ''
  const zip = invoice ? invoice?.zip : ''
  const city = invoice ? invoice?.city : ''
  const country_code = invoice ? invoice?.country_code : ''

  const country_name = country_code
    ? t(`countries:${country_code.toLowerCase()}`)
    : undefined

  const { isInvoiceRequired, needsInvoice, getDetailStatus } =
    useConfirmCustomer()

  const isSchemaValid = useMemo(() => {
    if (!invoice) {
      return false
    }
    return InvoiceFormSchema.safeParse(invoice).success
  }, [invoice])

  const isInvoiceEnabled = enabled || isInvoiceRequired

  const lines = [
    [first_name, last_name].join(' '),
    vat,
    email,
    mobile,
    [street, street2].join(' '),
    [zip, city].join(' '),
    [country_name, country_code].join(' '),
  ]

  const hasData = hasTextPaperLines(lines)
  const isEmpty = !hasData || !isInvoiceEnabled
  const isValid = (() => {
    if (!isInvoiceEnabled) {
      return true
    }
    if (isInvoiceRequired && needsInvoice) {
      return false
    }
    return isSchemaValid
  })()

  const status = getDetailStatus({
    hasData: !isEmpty,
    isValid,
  })

  return (
    <TextPaper
      icon="file-invoice"
      label={t('modals:invoice.title')}
      lines={lines}
      onPress={onPress}
      status={status}
    />
  )
}
